import { LoadingState } from "molecules/loadingState/LoadingState";
import { MissingDataState } from "molecules/missingDataState/MissingDataState";
import type {
	BaseQueryFn,
	TypedUseQueryHookResult,
} from "@reduxjs/toolkit/query/react";
import { useTranslation } from "react-i18next";

const supportEmail = "support@exopen.se";

type BaseProps<ReturnType, Params> = {
	query: TypedUseQueryHookResult<ReturnType, Params, BaseQueryFn>;
	renderLoading?: () => React.ReactNode;
	renderError?: (error: unknown) => React.ReactNode;
};

type PropsAllowError<ReturnType, Params> = BaseProps<ReturnType, Params> & {
	forceRenderOnError: true;
	render: (
		data: ReturnType | undefined,
		params: { isFetching: boolean },
	) => React.ReactNode;
};

type PropsNoError<ReturnType, Params> = BaseProps<ReturnType, Params> & {
	forceRenderOnError?: false;
	render: (
		data: ReturnType,
		params: { isFetching: boolean },
	) => React.ReactNode;
};

type Props<ReturnType, Params> =
	| PropsAllowError<ReturnType, Params>
	| PropsNoError<ReturnType, Params>;

export function QueryResult<ReturnType, Params>(
	props: Props<ReturnType, Params>,
) {
	const { t } = useTranslation();
	const defaultError = (
		<>
			{t("Something went wrong. Try reloading the page, or contact support:")}{" "}
			<a className="underline" href={`mailto:${supportEmail}`}>
				{supportEmail}
			</a>
		</>
	);
	const {
		query,
		renderLoading = () => <LoadingState />,
		renderError = () => (
			<MissingDataState title={t("No data found")} description={defaultError} />
		),
	} = props;

	if (query.isLoading) {
		return <>{renderLoading()}</>;
	}

	if (props.forceRenderOnError) {
		return <>{props.render(query.data, { isFetching: query.isFetching })}</>;
	}

	if (query.isError || !query.data) {
		return <>{renderError(query.error)}</>;
	}

	return <>{props.render(query.data, { isFetching: query.isFetching })}</>;
}
